import React from "react";
import { Link } from "react-router-dom";
import { Instagram, MapPin, Phone, Mail, Clock } from "lucide-react";
import { BRAND, HOURS } from "../mock";
import OpenStatusBadge from "./OpenStatusBadge";

const YelpIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" {...props}>
    <path d="M12.271 15.647l4.995 2.24c.526.237.62.94.17 1.302l-.001.001c-1.19.96-2.66 1.586-4.24 1.75-.55.058-.99-.44-.9-.985l.79-4.055c.06-.31.4-.485.686-.253l-.007-.001-.283-.001zm-2.02.99l1.16 5.31c.12.55-.36 1.04-.91.93-1.62-.32-3.09-1.06-4.28-2.11-.42-.37-.31-1.06.19-1.28l4.99-2.22c.29-.13.6.08.66.39l-.01-.01.21.99zm-.9-3.14L4.44 10.9c-.52-.22-.63-.9-.21-1.28C5.42 8.56 6.9 7.82 8.52 7.5c.55-.11 1.03.38.91.93l-1.16 5.31c-.06.31-.37.52-.66.39l.01.01-.99-.44zm3.03-1.42l-1.6-6.66c-.13-.55.33-1.06.89-.96 2.02.36 3.83 1.34 5.2 2.74.4.41.24 1.09-.29 1.29l-3.51 1.35c-.29.11-.59-.1-.65-.4l.01.01-.16.29z"/>
  </svg>
);

export default function Footer() {
  return (
    <footer className="relative bg-nala-charcoal border-t border-nala-gold/15 text-nala-cream">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <img src={BRAND.logos.gold} alt="Nala" className="h-16 w-auto mb-5" />
            <p className="font-serif-lux text-nala-cream-muted text-lg leading-relaxed italic">
              Atlanta's premier dining & entertainment destination.
            </p>
            <div className="flex items-center gap-3 mt-6">
              <a href={BRAND.social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram"
                 className="w-10 h-10 rounded-full border border-nala-gold/40 flex items-center justify-center text-nala-gold hover:bg-nala-gold hover:text-nala-black transition-all duration-300">
                <Instagram size={17} />
              </a>
              <a href={BRAND.social.yelp} target="_blank" rel="noreferrer" aria-label="Yelp"
                 className="w-10 h-10 rounded-full border border-nala-gold/40 flex items-center justify-center text-nala-gold hover:bg-nala-gold hover:text-nala-black transition-all duration-300">
                <YelpIcon />
              </a>
              <a href={BRAND.social.google} target="_blank" rel="noreferrer" aria-label="Google"
                 className="w-10 h-10 rounded-full border border-nala-gold/40 flex items-center justify-center text-nala-gold hover:bg-nala-gold hover:text-nala-black transition-all duration-300">
                <MapPin size={17} />
              </a>
            </div>
          </div>

          {/* Explore */}
          <div>
            <h4 className="font-display text-sm tracking-luxe text-nala-gold mb-5 uppercase">Explore</h4>
            <ul className="space-y-3 text-sm text-nala-cream-muted">
              {[
                { l: "Home", to: "/" },
                { l: "Menu", to: "/menu" },
                { l: "Reservations", to: "/reservations" },
                { l: "Private Events", to: "/private-events" },
                { l: "Contact", to: "/contact" },
              ].map((i) => (
                <li key={i.to}>
                  <Link to={i.to} className="hover:text-nala-gold transition-colors tracking-wide-lux">{i.l}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="font-display text-sm tracking-luxe text-nala-gold mb-5 uppercase flex items-center gap-2">
              <Clock size={15} /> Hours
            </h4>
            <div className="mb-4"><OpenStatusBadge /></div>
            <ul className="space-y-3 text-sm text-nala-cream-muted">
              {HOURS.map((h) => (
                <li key={h.day} className="flex flex-col">
                  <span className="text-nala-cream">{h.day}</span>
                  <span className="text-nala-gold/80">{h.time}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display text-sm tracking-luxe text-nala-gold mb-5 uppercase">Contact</h4>
            <ul className="space-y-4 text-sm text-nala-cream-muted">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-nala-gold mt-0.5 shrink-0" />
                <span>{BRAND.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={16} className="text-nala-gold shrink-0" />
                <a href={BRAND.phoneHref} className="hover:text-nala-gold transition-colors">{BRAND.phone}</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={16} className="text-nala-gold shrink-0" />
                <a href={`mailto:${BRAND.email}`} className="hover:text-nala-gold transition-colors break-all">{BRAND.email}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-7 border-t border-white/8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-nala-cream-muted tracking-wide-lux">
          <p>© {new Date().getFullYear()} Nala Kitchen & Cocktails. All Rights Reserved.</p>
          <p className="uppercase">Please Drink Responsibly · Must be 21+</p>
        </div>

        <div className="mt-5 pt-5 border-t border-white/5 text-center">
          <p className="text-xs text-nala-cream-muted/70 tracking-wide-lux uppercase">
            Website Built and Managed by{" "}
            <a
              href="https://webnehtech.com"
              target="_blank"
              rel="noreferrer"
              className="text-nala-gold hover:text-nala-gold-light transition-colors"
            >
              WEBNEHTECH.COM
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
